// v2/pages/admin/AdminUsersV2.js — Yonetim / Kullanicilar (/v2/admin/users)
// URL: ?q= &code= &state= &role= &online= &activity= &sort= &dir= &page= &per=
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  UserCog, Search, X, SlidersHorizontal, RotateCw, ChevronLeft, ChevronRight,
  ChevronDown, ChevronUp, ChevronsUpDown, Users, UserX, Wifi, Clock,
  AlertTriangle, Inbox, Mail, Store, Pencil, Trash2, Power, PowerOff,
  Eye, EyeOff, Copy, Check, Loader2, KeyRound, ShieldCheck,
} from 'lucide-react';
import { useAdminUsers } from '../../hooks/useAdminUsers';
import { adminUserApi } from '../../hooks/useAdminCustomer';
import UserFormModal from '../../components/admin/UserFormModal';
import ToastStack from '../../components/Toast';
import useToast from '../../hooks/useToast';
import { formatNumber } from '../../utils/format';
import { zamanTR, basHarfler } from '../../utils/adminFormat';
import { SkelPanel } from '../../components/Skeleton';
import s from '../../styles/AdminUsersV2.module.css';
import '../../styles/tokens.css';

const DURUMLAR = [
  { id: '', label: 'Tüm durumlar' },
  { id: 'active', label: 'Aktif' },
  { id: 'passive', label: 'Pasif' },
];
const ROLLER = [
  { id: '', label: 'Tüm roller' },
  { id: 'user', label: 'Müşteri kullanıcısı' },
  { id: 'admin', label: 'Yönetici' },
];
const ETKINLIK = [
  { id: '', label: 'Tüm kullanıcılar' },
  { id: 'recent', label: 'Son 30 günde giren' },
  { id: 'idle', label: '90 gündür girmeyen' },
  { id: 'never', label: 'Hiç giriş yapmamış' },
];
const KOLONLAR = [
  { id: 'name', label: 'Kullanıcı', sort: 'name' },
  { id: 'cust', label: 'Müşteri', sort: 'code' },
  { id: 'logins', label: 'Giriş', sort: 'logins', num: true },
  { id: 'last', label: 'Son Giriş', sort: 'last_login' },
  { id: 'pw', label: 'Parola' },
  { id: 'state', label: 'Durum' },
  { id: 'act', label: '' },
];
const VARSAYILAN = { sort: 'last_login', dir: 'desc', page: '1', per: '25' };

/** Parolayi gosterip kopyalar */
function Parola({ kullanici, onHata }) {
  const [parola, setParola] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [kopyalandi, setKopyalandi] = useState(false);

  const getir = useCallback(async () => {
    if (parola !== null) { setParola(null); return; }
    setYukleniyor(true);
    try {
      const v = await adminUserApi.parola(kullanici.id);
      if (!v || !v.success) throw new Error((v && v.message) || 'Parola alınamadı');
      setParola(v.password || '');
    } catch (e) {
      onHata((e && e.message) || 'Parola alınamadı');
    } finally {
      setYukleniyor(false);
    }
  }, [parola, kullanici.id, onHata]);

  const kopyala = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(parola || '');
      setKopyalandi(true);
      setTimeout(() => setKopyalandi(false), 1600);
    } catch (e) { onHata('Kopyalanamadı'); }
  }, [parola, onHata]);

  if (kullanici.is_admin) return <span className={s.dim}>-</span>;

  return (
    <span className={s.pwCell}>
      <button type="button" className={s.pwBtn} onClick={getir} disabled={yukleniyor}
        title={parola === null ? 'Parolayı göster' : 'Gizle'}>
        {yukleniyor ? <Loader2 size={13} className={s.spin} />
          : parola === null ? <Eye size={13} /> : <EyeOff size={13} />}
        <span className={s.pwText}>{parola === null ? '••••••' : (parola || '(boş)')}</span>
      </button>
      {parola !== null && parola && (
        <button type="button" className={s.copyBtn} onClick={kopyala} title="Kopyala">
          {kopyalandi ? <Check size={13} className={s.okIcon} /> : <Copy size={13} />}
        </button>
      )}
    </span>
  );
}

export default function AdminUsersV2() {
  const [sp, setSp] = useSearchParams();
  const oku = useCallback((k) => sp.get(k) || VARSAYILAN[k] || '', [sp]);
  const { items: toastlar, push, dismiss } = useToast();

  const [arama, setArama] = useState(sp.get('q') || '');
  const [filtreAcik, setFiltreAcik] = useState(false);
  const [duzenlenen, setDuzenlenen] = useState(null);
  const [silinecek, setSilinecek] = useState(null);
  const [islemde, setIslemde] = useState(false);

  useEffect(() => { setArama(sp.get('q') || ''); }, [sp]);

  const params = useMemo(() => ({
    q: sp.get('q') || '', code: sp.get('code') || '', state: sp.get('state') || '',
    role: sp.get('role') || '', online: sp.get('online') || '',
    activity: sp.get('activity') || '',
    sort: oku('sort'), dir: oku('dir'), page: oku('page'), per: oku('per'),
  }), [sp, oku]);

  const { data, loading, error, yenile } = useAdminUsers(params);

  const guncelle = useCallback((yeni, sayfaSifirla = true) => {
    setSp((onceki) => {
      const p = new URLSearchParams(onceki);
      Object.entries(yeni).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined || v === VARSAYILAN[k]) p.delete(k);
        else p.set(k, v);
      });
      if (sayfaSifirla) p.delete('page');
      return p;
    }, { replace: true });
  }, [setSp]);

  useEffect(() => {
    const suAn = sp.get('q') || '';
    if (arama === suAn) return undefined;
    const t = setTimeout(() => guncelle({ q: arama.trim() }), 400);
    return () => clearTimeout(t);
  }, [arama, sp, guncelle]);

  const sirala = (alan) => {
    if (!alan) return;
    const ayni = params.sort === alan;
    guncelle({ sort: alan, dir: ayni && params.dir === 'desc' ? 'asc' : 'desc' });
  };

  const durumDegistir = useCallback(async (u) => {
    setIslemde(true);
    try {
      const v = await adminUserApi.guncelle(u.id, { durum: !u.active });
      if (!v || !v.success) throw new Error((v && v.message) || 'İşlem başarısız');
      push(u.active ? 'Kullanıcı pasife alındı' : 'Kullanıcı aktifleştirildi', 'success');
      yenile();
    } catch (e) {
      push((e && e.message) || 'İşlem başarısız', 'error');
    } finally { setIslemde(false); }
  }, [push, yenile]);

  const sil = useCallback(async () => {
    if (!silinecek) return;
    setIslemde(true);
    try {
      const v = await adminUserApi.sil(silinecek.id);
      if (!v || !v.success) throw new Error((v && v.message) || 'Silinemedi');
      push('Kullanıcı silindi', 'success');
      setSilinecek(null);
      yenile();
    } catch (e) {
      push((e && e.message) || 'Silinemedi', 'error');
    } finally { setIslemde(false); }
  }, [silinecek, push, yenile]);

  const ilk = loading && !data;
  const ogeler = (data && data.items) || [];
  const o = (data && data.summary) || {};
  const sayfa = (data && data.page) || 1;
  const sonSayfa = (data && data.total_pages) || 1;
  const toplam = (data && data.total) || 0;
  const per = Number(params.per) || 25;
  const ilkSira = (sayfa - 1) * per;
  const acikFiltre = ['code', 'state', 'role', 'online', 'activity']
    .filter((k) => params[k]).length;

  const SiraIkon = ({ alan }) => {
    if (!alan) return null;
    if (params.sort !== alan) return <ChevronsUpDown size={13} className={s.sortIdle} />;
    return params.dir === 'asc'
      ? <ChevronUp size={13} className={s.sortOn} />
      : <ChevronDown size={13} className={s.sortOn} />;
  };

  const kart = (ikon, ton, deger, etiket, filtre) => {
    const Ikon = ikon;
    const acik = filtre && Object.entries(filtre).every(([k, v]) => params[k] === v);
    const icerik = (
      <>
        <span className={`${s.statIcon} ${s[ton]}`}><Ikon size={17} /></span>
        <span className={s.statBody}>
          <span className={s.statValue}>{formatNumber(deger)}</span>
          <span className={s.statLabel}>{etiket}</span>
        </span>
      </>
    );
    if (!filtre) return <div className={s.stat}>{icerik}</div>;
    return (
      <button type="button" className={`${s.stat} ${s.statBtn} ${acik ? s.statOn : ''}`}
        onClick={() => guncelle(acik
          ? Object.fromEntries(Object.keys(filtre).map((k) => [k, '']))
          : filtre)}>
        {icerik}
      </button>
    );
  };

  return (
    <div className={s.page}>
      <div className={s.head}>
        <div className={s.headText}>
          <h2 className={s.title}>Kullanıcılar</h2>
          <p className={s.subtitle}>
            {ilk ? 'Yükleniyor…'
              : <><b>{formatNumber(toplam)}</b> kullanıcı · <b>{formatNumber(o.customers)}</b> cari</>}
          </p>
        </div>
        <button type="button" className={s.ghostBtn} onClick={yenile} disabled={loading}>
          <RotateCw size={15} className={loading ? s.spin : ''} />
          <span className={s.btnText}>Yenile</span>
        </button>
      </div>

      <div className={s.stats}>
        {kart(Users, 'tBlue', o.active, 'Aktif kullanıcı', { state: 'active' })}
        {kart(Wifi, 'tGreen', o.online, 'Oturumu açık', { online: '1' })}
        {kart(Clock, 'tAmber', o.idle, `${o.idle_days || 90} gündür girmeyen`, { activity: 'idle' })}
        {kart(UserX, 'tSlate', o.never, 'Hiç giriş yapmamış', { activity: 'never' })}
      </div>

      <div className={s.toolbar}>
        <div className={s.searchBox}>
          <Search size={16} className={s.searchIcon} />
          <input type="search" className={s.search} placeholder="Ad, e-posta, cari kodu veya ünvanı…"
            value={arama} onChange={(e) => setArama(e.target.value)} aria-label="Kullanıcı ara" />
          {arama && (
            <button type="button" className={s.clearBtn} onClick={() => setArama('')} aria-label="Temizle">
              <X size={15} />
            </button>
          )}
        </div>
        <button type="button" className={`${s.ghostBtn} ${filtreAcik ? s.ghostOn : ''}`}
          onClick={() => setFiltreAcik((v) => !v)} aria-expanded={filtreAcik}>
          <SlidersHorizontal size={15} />
          <span className={s.btnText}>Filtreler</span>
          {acikFiltre > 0 && <span className={s.badge}>{acikFiltre}</span>}
        </button>
      </div>

      {filtreAcik && (
        <div className={s.filters}>
          <label className={s.field}>
            <span className={s.fieldLabel}>Durum</span>
            <select className={s.select} value={params.state}
              onChange={(e) => guncelle({ state: e.target.value })}>
              {DURUMLAR.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Rol</span>
            <select className={s.select} value={params.role}
              onChange={(e) => guncelle({ role: e.target.value })}>
              {ROLLER.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Etkinlik</span>
            <select className={s.select} value={params.activity}
              onChange={(e) => guncelle({ activity: e.target.value })}>
              {ETKINLIK.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </label>
          <div className={s.filterActions}>
            <button type="button"
              className={`${s.toggle} ${params.online === '1' ? s.toggleOn : ''}`}
              onClick={() => guncelle({ online: params.online === '1' ? '' : '1' })}>
              <Wifi size={14} /> Oturumu açık
            </button>
            {(acikFiltre > 0 || params.q) && (
              <button type="button" className={s.resetBtn} onClick={() => setSp({}, { replace: true })}>
                <X size={14} /> Temizle
              </button>
            )}
          </div>
        </div>
      )}

      {params.code && (
        <div className={s.chipRow}>
          <span className={s.filterChip}>
            <Store size={13} /> Cari: <b>{params.code}</b>
            <button type="button" onClick={() => guncelle({ code: '' })} aria-label="Kaldır"><X size={13} /></button>
          </span>
        </div>
      )}

      {error && (
        <div className={s.alert}>
          <AlertTriangle size={18} /> <span>{error}</span>
          <button type="button" className={s.alertBtn} onClick={yenile}>Tekrar dene</button>
        </div>
      )}

      <section className={s.panel}>
        {loading && !ilk && <span className={s.loadBar} aria-hidden="true" />}
        {ilk ? (
          <div className={s.panelBody}><SkelPanel rows={7} head={false} /></div>
        ) : ogeler.length === 0 ? (
          <div className={s.empty}>
            <Inbox size={24} />
            <p className={s.emptyTitle}>Kullanıcı bulunamadı</p>
            <p className={s.emptyText}>Arama veya filtreleri değiştirmeyi deneyin.</p>
          </div>
        ) : (
          <>
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th className={`${s.th} ${s.thNo}`}>#</th>
                    {KOLONLAR.map((k) => (
                      <th key={k.id}
                        className={`${s.th} ${k.num ? s.num : ''} ${k.sort ? s.thSort : ''}`}
                        onClick={() => sirala(k.sort)}>
                        <span className={s.thInner}>{k.label}<SiraIkon alan={k.sort} /></span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ogeler.map((u, i) => (
                    <React.Fragment key={u.id}>
                      <tr className={u.active ? '' : s.rowOff}>
                        <td className={`${s.td} ${s.tdNo}`}>{ilkSira + i + 1}</td>
                        <td className={s.td}>
                          <span className={s.userCell}>
                            <span className={s.avatar}>{basHarfler(u.ad_soyad)}</span>
                            <span className={s.userText}>
                              <span className={s.userName}>{u.ad_soyad}</span>
                              <span className={s.userMail} title={u.mail}>{u.mail}</span>
                            </span>
                          </span>
                        </td>
                        <td className={s.td}>
                          {u.code ? (
                            <span className={s.cell}>
                              <Link to={`/v2/admin/customers/${encodeURIComponent(u.code)}`}
                                className={s.custLink} title={u.customer_name || ''}>
                                {u.customer_name || u.code}
                              </Link>
                              <span className={s.sub}>{u.code}</span>
                            </span>
                          ) : <span className={s.dim}>-</span>}
                        </td>
                        <td className={`${s.td} ${s.num}`}>{formatNumber(u.logins)}</td>
                        <td className={`${s.td} ${s.muted}`}>
                          {u.last_login ? zamanTR(u.last_login)
                            : <span className={s.never}>Hiç girmemiş</span>}
                        </td>
                        <td className={s.td}><Parola kullanici={u} onHata={(m) => push(m, 'error')} /></td>
                        <td className={s.td}>
                          <span className={s.pills}>
                            {u.is_admin && <span className={`${s.pill} ${s.pillNavy}`}>Yönetici</span>}
                            <span className={`${s.pill} ${u.active ? s.pillGreen : s.pillGray}`}>
                              {u.active ? 'Aktif' : 'Pasif'}
                            </span>
                            {u.online && <span className={`${s.pill} ${s.pillBlue}`}>Açık</span>}
                          </span>
                        </td>
                        <td className={`${s.td} ${s.tdAct}`}>
                          {!u.is_admin && (
                            <span className={s.actions}>
                              <button type="button" className={s.actBtn} title="Düzenle"
                                onClick={() => setDuzenlenen(u)}><Pencil size={14} /></button>
                              <button type="button" className={s.actBtn} disabled={islemde}
                                title={u.active ? 'Pasife al' : 'Aktifleştir'}
                                onClick={() => durumDegistir(u)}>
                                {u.active ? <PowerOff size={14} /> : <Power size={14} />}
                              </button>
                              <button type="button" className={`${s.actBtn} ${s.actDanger}`}
                                title="Sil" onClick={() => setSilinecek(u)}><Trash2 size={14} /></button>
                            </span>
                          )}
                        </td>
                      </tr>
                      {silinecek && silinecek.id === u.id && (
                        <tr className={s.confirmRow}>
                          <td className={s.confirmTd} colSpan={8}>
                            <div className={s.confirm}>
                              <AlertTriangle size={16} />
                              <span className={s.confirmText}>
                                <b>{u.ad_soyad}</b> kalıcı olarak silinecek. Bu işlem geri alınamaz.
                              </span>
                              <button type="button" className={s.confirmNo}
                                onClick={() => setSilinecek(null)} disabled={islemde}>Vazgeç</button>
                              <button type="button" className={s.confirmYes} onClick={sil} disabled={islemde}>
                                {islemde ? <Loader2 size={14} className={s.spin} /> : <Trash2 size={14} />} Evet, sil
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className={s.mList}>
              {ogeler.map((u) => (
                <li key={`m-${u.id}`} className={`${s.mItem} ${u.active ? '' : s.rowOff}`}>
                  <div className={s.mTop}>
                    <span className={s.avatar}>{basHarfler(u.ad_soyad)}</span>
                    <span className={s.userText}>
                      <span className={s.userName}>{u.ad_soyad}</span>
                      <span className={s.userMail}>{u.mail}</span>
                    </span>
                    <span className={s.pills}>
                      <span className={`${s.pill} ${u.active ? s.pillGreen : s.pillGray}`}>
                        {u.active ? 'Aktif' : 'Pasif'}
                      </span>
                    </span>
                  </div>
                  <div className={s.mMeta}>
                    {u.code && (
                      <Link to={`/v2/admin/customers/${encodeURIComponent(u.code)}`} className={s.mLink}>
                        <Store size={12} /> {u.customer_name || u.code}
                      </Link>
                    )}
                    <span>{formatNumber(u.logins)} giriş</span>
                    <span>{u.last_login ? zamanTR(u.last_login) : 'Hiç girmemiş'}</span>
                  </div>
                  <div className={s.mFoot}>
                    <Parola kullanici={u} onHata={(m) => push(m, 'error')} />
                    {!u.is_admin && (
                      <span className={s.actions}>
                        <button type="button" className={s.actBtn} onClick={() => setDuzenlenen(u)}>
                          <Pencil size={14} />
                        </button>
                        <button type="button" className={s.actBtn} disabled={islemde}
                          onClick={() => durumDegistir(u)}>
                          {u.active ? <PowerOff size={14} /> : <Power size={14} />}
                        </button>
                        <button type="button" className={`${s.actBtn} ${s.actDanger}`}
                          onClick={() => setSilinecek(u)}><Trash2 size={14} /></button>
                      </span>
                    )}
                  </div>
                  {silinecek && silinecek.id === u.id && (
                    <div className={s.confirm}>
                      <AlertTriangle size={16} />
                      <span className={s.confirmText}>Kalıcı olarak silinecek.</span>
                      <button type="button" className={s.confirmNo}
                        onClick={() => setSilinecek(null)} disabled={islemde}>Vazgeç</button>
                      <button type="button" className={s.confirmYes} onClick={sil} disabled={islemde}>
                        {islemde ? <Loader2 size={14} className={s.spin} /> : <Trash2 size={14} />} Sil
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>

            <div className={s.pager}>
              <span className={s.pagerInfo}>
                {formatNumber(ilkSira + 1)}–{formatNumber(ilkSira + ogeler.length)} / {formatNumber(toplam)}
              </span>
              <label className={s.perBox}>
                <span className={s.fieldLabel}>Sayfa başına</span>
                <select className={s.selectSm} value={params.per}
                  onChange={(e) => guncelle({ per: e.target.value })}>
                  {((data && data.page_sizes) || [25, 50, 100]).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <div className={s.pagerBtns}>
                <button type="button" className={s.pageBtn} disabled={sayfa <= 1 || loading}
                  onClick={() => guncelle({ page: String(sayfa - 1) }, false)} aria-label="Önceki">
                  <ChevronLeft size={16} />
                </button>
                <span className={s.pageNo}>{formatNumber(sayfa)} / {formatNumber(sonSayfa)}</span>
                <button type="button" className={s.pageBtn} disabled={sayfa >= sonSayfa || loading}
                  onClick={() => guncelle({ page: String(sayfa + 1) }, false)} aria-label="Sonraki">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      <UserFormModal acik={Boolean(duzenlenen)} kod={duzenlenen && duzenlenen.code}
        kullanici={duzenlenen ? { ...duzenlenen, phone: duzenlenen.phone } : null}
        onKapat={() => setDuzenlenen(null)}
        onKaydedildi={(mesaj) => { setDuzenlenen(null); push(mesaj, 'success'); yenile(); }} />
      <ToastStack items={toastlar} onDismiss={dismiss} />
    </div>
  );
}
